/**
 * MissionThreadPanel — the wired container around the reusable <MessageThread> for a single
 * drone mission (Wave-2 Phase 1 #346). Parameterized by the load / send / markRead fns, so the
 * SAME container drives the gov (COP) side and the operator (portal) side; the pure thread UI is
 * shared. It loads the thread on open, auto-marks inbound messages read (clearing the viewer's
 * unread), posts new messages, and — for the gov — renders a "Call pilot" tel: link.
 */
import { useEffect, useState, type JSX } from 'react';
import type { MissionMessageView, ThreadParty } from '../api';
import { MessageThread } from './MessageThread';

/** Has the VIEWER read this message already? (inbound read-receipt for THIS party) */
function viewerHasRead(m: MissionMessageView, role: ThreadParty): boolean {
  return role === 'requestor' ? m.read_by_requestor_at != null : m.read_by_operator_at != null;
}

/** Stamp the viewer's read column locally so the unread state stays consistent post-mark. */
function stampViewerRead(m: MissionMessageView, role: ThreadParty): MissionMessageView {
  return role === 'requestor'
    ? { ...m, read_by_requestor_at: m.read_by_requestor_at ?? m.created_at }
    : { ...m, read_by_operator_at: m.read_by_operator_at ?? m.created_at };
}

export function MissionThreadPanel({
  missionId,
  viewerRole,
  load,
  send,
  markRead,
  callPhone,
  disabled = false,
  disabledNote,
  onReadMarked,
  partyLabels,
  headingLabel,
  className,
}: {
  missionId: string;
  viewerRole: ThreadParty;
  load: (signal?: AbortSignal) => Promise<MissionMessageView[]>;
  send: (content: string) => Promise<MissionMessageView>;
  markRead: (msgId: string) => Promise<void>;
  /** Gov side: the assigned pilot's call-back number → a "Call pilot" tel: link. */
  callPhone?: string | null;
  disabled?: boolean;
  disabledNote?: string;
  /** Called after inbound messages are marked read, so a parent can refresh its unread badge. */
  onReadMarked?: () => void;
  /** Author-role → display label, forwarded to <MessageThread>. A non-mission container passes
   *  its own so a message never renders as "Pilot" out of context (default = gov↔pilot). */
  partyLabels?: Record<ThreadParty, string>;
  /** Section heading (default "Messages"). */
  headingLabel?: string;
  className?: string;
}): JSX.Element {
  const [messages, setMessages] = useState<MissionMessageView[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;

    async function markInbound(msgs: MissionMessageView[]): Promise<void> {
      const unread = msgs.filter((m) => m.author_role !== viewerRole && !viewerHasRead(m, viewerRole));
      if (unread.length === 0) return;
      for (const m of unread) {
        try {
          await markRead(m.id);
        } catch {
          /* best-effort — the badge just stays until the next reload */
        }
      }
      if (!alive) return;
      const ids = new Set(unread.map((u) => u.id));
      setMessages((prev) => prev.map((m) => (ids.has(m.id) ? stampViewerRead(m, viewerRole) : m)));
      onReadMarked?.();
    }

    void load(ac.signal)
      .then((msgs) => {
        if (!alive) return;
        setMessages(msgs);
        void markInbound(msgs);
      })
      .catch(() => {});

    return () => {
      alive = false;
      ac.abort();
    };
    // Reload when the mission changes. load/send/markRead are stable per-mission closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId, viewerRole]);

  async function handleSend(content: string): Promise<void> {
    setSending(true);
    try {
      const created = await send(content);
      setMessages((prev) => [...prev, created]);
    } catch {
      // On failure, re-pull the authoritative thread.
      try {
        setMessages(await load());
      } catch {
        /* keep current */
      }
    } finally {
      setSending(false);
    }
  }

  const telHref = callPhone ? `tel:${callPhone.replace(/[^\d+]/g, '')}` : null;

  return (
    <section className={`border-t border-slate-800 pt-3 ${className ?? ''}`} data-testid="mission-thread-panel">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="text-xs uppercase tracking-wider text-slate-400">{headingLabel ?? 'Messages'}</h3>
        {telHref && (
          <a
            href={telHref}
            data-testid="call-pilot"
            className="rounded border border-emerald-500/50 px-2 py-0.5 text-[11px] font-medium text-emerald-300 hover:bg-emerald-500/10"
            title={`Call the pilot at ${callPhone}`}
          >
            📞 Call pilot
          </a>
        )}
      </div>
      <MessageThread
        messages={messages}
        viewerRole={viewerRole}
        onSend={(c) => void handleSend(c)}
        sending={sending}
        disabled={disabled}
        disabledNote={disabledNote}
        partyLabels={partyLabels}
      />
    </section>
  );
}
